import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import {
  setFilter,
  setCurrentPageNumber,
  getHomeImageData,
  setReportFilter,
  setReportCurrentPageNumber,
  getReportData,
} from "../../actions";

export const SAITAMA_INSPECTION_ITEMS = [
  { code: "UNINSPECTED", label: "未点検" },
  { code: "NO_ABNORMALITY", label: "異常なし" },
  { code: "BODY_TILT", label: "本体の傾き" },
  { code: "BODY_RUST_CORROSION", label: "本体の錆び・腐食" },
  { code: "BODY_ATTACHMENT", label: "本体への付着（貼り紙・落書き・ツル草など）" },
  { code: "LAMP_DAMAGE", label: "灯具の割れ・破損" },
];

export const parseSaitamaSort = (sortValue) => {
  const separatorIndex = String(sortValue || "").lastIndexOf("-");
  return {
    type: String(sortValue || "").slice(0, separatorIndex),
    order: String(sortValue || "").slice(separatorIndex + 1),
  };
};

const useStyles = makeStyles((theme) => ({
  container: { width: 500, height: "100vh", padding: 16, boxSizing: "border-box", position: "relative" },
  scrollArea: { height: "calc(100vh - 130px)", overflowY: "auto", padding: 16 },
  filterItem: { marginTop: theme.spacing(3) },
  formControl: { margin: theme.spacing(1), width: "89%" },
  reportSortControl: { width: "100%" },
  buttonContainer: { position: "absolute", bottom: 45, left: "50%", transform: "translateX(-50%)", width: "100%", display: "flex", justifyContent: "space-around" },
  dataBtn: { minWidth: 190, height: theme.spacing(4), paddingTop: theme.spacing(1.5) },
  title: { marginLeft: 42 },
}));

export const FilterDrawerSaitama = ({
  toggleDrawer,
  anchor,
  filterStore,
  reportFilterStore,
  reportTagFilterData,
  filterType,
  setFilter,
  setCurrentPageNumber,
  getHomeImageData,
  setReportFilter,
  setReportCurrentPageNumber,
  getReportData,
}) => {
  const classes = useStyles();
  const isReport = filterType === "report";
  const [managementId, setManagementId] = useState("");
  const [inspectionItems, setInspectionItems] = useState([]);
  const [firstCaptureOnly, setFirstCaptureOnly] = useState(false);
  const [reportTags, setReportTags] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [sort, setSort] = useState("occurred_datetime-desc");

  useEffect(() => {
    const activeFilter = isReport ? reportFilterStore : filterStore;
    setManagementId(activeFilter.managementId || "");
    setInspectionItems(activeFilter.inspectionItems || []);
    setFirstCaptureOnly(Boolean(activeFilter.firstCaptureOnly));
    setReportTags(activeFilter.tag || []);
    setPlayerId(activeFilter.playerId || "");
    setSort(`${isReport ? "occurred_datetime" : (activeFilter.type || "occurred_datetime")}-${activeFilter.order || "desc"}`);
  }, [filterStore, reportFilterStore, isReport]);

  const apply = (nextFilter) => {
    if (isReport) {
      setReportFilter({ ...nextFilter, filterType: "report" });
      setReportCurrentPageNumber(1);
      getReportData({ pageNumber: 1 });
    } else {
      setFilter({ ...nextFilter, filterType: "photo" });
      setCurrentPageNumber(1);
      getHomeImageData({ pageNumber: 1 });
    }
    toggleDrawer(anchor, false)();
  };

  const handleApply = () => {
    const { type, order } = parseSaitamaSort(sort);
    if (isReport) {
      apply({ ...reportFilterStore, type, order, managementId, tag: reportTags, playerId });
    } else {
      apply({ ...filterStore, type, order, managementId, inspectionItems, firstCaptureOnly });
    }
  };

  const handleReset = () => {
    if (isReport) {
      apply({ ...reportFilterStore, type: "occurred_datetime", order: "desc", managementId: "", tag: [], playerId: "" });
    } else {
      apply({
        ...filterStore,
        type: "occurred_datetime",
        order: "desc",
        managementId: "",
        inspectionItems: [],
        firstCaptureOnly: false,
      });
    }
  };

  return (
    <div className={classes.container}>
      <div style={{ display: "flex", alignItems: "center", padding: "8px 0" }}>
        <IconButton onClick={toggleDrawer(anchor, false)} aria-label="close filter"><CloseIcon /></IconButton>
        <Typography variant="h6" className={classes.title}>絞り込み条件設定</Typography>
      </div>
      <div className={classes.scrollArea}>
        <Grid container direction="column">
          {isReport ? (
            <>
              <Grid item className={classes.filterItem}>
                <TextField label="管理ID" variant="outlined" fullWidth value={managementId} onChange={(e) => setManagementId(e.target.value)} />
              </Grid>
              <Grid item className={classes.filterItem}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={reportTagFilterData || []}
                  value={(reportTagFilterData || []).filter((item) => reportTags.includes(item.value))}
                  getOptionLabel={(option) => option.value || ""}
                  getOptionSelected={(option, value) => option.value === value.value}
                  onChange={(_, values) => setReportTags(values.map((item) => item.value))}
                  renderTags={(values, getTagProps) => values.map((option, index) => <Chip {...getTagProps({ index })} key={option.value} label={option.label || option.value} />)}
                  renderInput={(params) => <TextField {...params} label="報告タグ" variant="outlined" />}
                  renderOption={(option) => (
                    <Typography variant="body1">
                      <b>{option.label || option.value}</b> : {option.tip || ""} ({option.count || 0})
                    </Typography>
                  )}
                />
              </Grid>
              <Grid item className={classes.filterItem}>
                <TextField label="Player ID" variant="outlined" fullWidth value={playerId} onChange={(e) => setPlayerId(e.target.value)} />
              </Grid>
              <Grid item className={classes.filterItem}>
                <FormControl variant="outlined" className={classes.reportSortControl}>
                  <InputLabel id="saitama-report-sort-label">ソート</InputLabel>
                  <Select labelId="saitama-report-sort-label" value={sort} onChange={(e) => setSort(e.target.value)} label="ソート">
                    <MenuItem value="occurred_datetime-desc">報告日時 - 降順</MenuItem>
                    <MenuItem value="occurred_datetime-asc">報告日時 - 昇順</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          ) : (
          <>
            <Grid item className={classes.filterItem}>
              <TextField label="管理ID" variant="outlined" fullWidth value={managementId} onChange={(e) => setManagementId(e.target.value)} />
            </Grid>
            <Grid item className={classes.filterItem}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={SAITAMA_INSPECTION_ITEMS}
                value={SAITAMA_INSPECTION_ITEMS.filter((item) => inspectionItems.includes(item.code))}
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) => option.code === value.code}
                onChange={(_, values) => setInspectionItems(values.map((item) => item.code))}
                renderOption={(option, { selected }) => (
                  <FormControlLabel control={<Checkbox checked={selected} />} label={option.label} />
                )}
                renderTags={(values, getTagProps) => values.map((option, index) => <Chip {...getTagProps({ index })} key={option.code} label={option.label} />)}
                renderInput={(params) => <TextField {...params} label="点検項目" variant="outlined" />}
              />
            </Grid>
          </>
          )}
          {!isReport && <Grid item className={classes.filterItem}>
            <FormControlLabel control={<Checkbox checked={firstCaptureOnly} onChange={(e) => setFirstCaptureOnly(e.target.checked)} />} label="初回撮影のみ表示" />
          </Grid>}
          {!isReport && <Grid item className={classes.filterItem}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="saitama-sort-label">ソート</InputLabel>
              <Select labelId="saitama-sort-label" value={sort} onChange={(e) => setSort(e.target.value)} label="ソート">
                <MenuItem value="occurred_datetime-desc">撮影日時 - 降順</MenuItem>
                <MenuItem value="occurred_datetime-asc">撮影日時 - 昇順</MenuItem>
              </Select>
            </FormControl>
          </Grid>}
        </Grid>
      </div>
      <div className={classes.buttonContainer}>
        <Button className={classes.dataBtn} variant="contained" color="primary" onClick={handleApply}>絞り込む</Button>
        <Button className={classes.dataBtn} variant="contained" onClick={handleReset}>リセット</Button>
      </div>
    </div>
  );
};

FilterDrawerSaitama.propTypes = { toggleDrawer: PropTypes.func.isRequired, anchor: PropTypes.string.isRequired };

export default connect(
  (state) => ({
    filterStore: state.home.filter,
    reportFilterStore: state.report.filter,
    reportTagFilterData: state.report.reportTagFilterData,
  }),
  (dispatch) => ({
    setFilter: (value) => dispatch(setFilter(value)),
    setCurrentPageNumber: (value) => dispatch(setCurrentPageNumber(value)),
    getHomeImageData: (value) => dispatch(getHomeImageData(value)),
    setReportFilter: (value) => dispatch(setReportFilter(value)),
    setReportCurrentPageNumber: (value) => dispatch(setReportCurrentPageNumber(value)),
    getReportData: (value) => dispatch(getReportData(value)),
  }),
)(FilterDrawerSaitama);
